import {Box, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import {AurDataGrid} from '../../common/aur-data-grid/AurDataGrid';
import {
  getClientManagementColumns,
  MANAGE_CLIENTS_TABLE_WIDTH,
} from '../../../utils/clientManagemetUtils';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import AurClientCard from '../../client-management/aur-client-card/AurClientCard';
import AurCardGrid from '../../common/aur-card-grid/AurCardGrid';
import {ClientCardProps, ClientData} from '../../../types/types';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {GridRowParams} from '@mui/x-data-grid';
import {ClientManagementTabStatus} from './models';
import {
  setClientListData,
  setSelectedClientId,
  resetClientList,
} from '../../../redux/slices/clientListSlice';
import {GridSortModel} from '@mui/x-data-grid';
import {getClientsList} from '../../../api/ClientManagementService';
import {RootState} from '../../../redux/store';
import {setManageClientsPageState} from '../../../redux/slices/pageViewStateSlice';
import AurNoResults from '../../common/aur-no-results/AurNoResults';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  failedToLoadStatusCodes,
  INVOICE_REPORT_GRID_ERROR_COMPONENT,
  MIN_RECORDS_FOR_CLIENT_GRID,
  MIN_SEARCH_CHAR,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import AurButton from '../../common/aur-button/AurButton';
import {scrollToGridView} from '../../../utils/utils';

// Default list-view when null/table so the first request uses grid pagination (not card pageSize 12).
const resolveViewButton = (viewMode: 'table' | 'tile' | null): string =>
  viewMode === 'tile' ? 'grid-view' : 'list-view';

const ClientManagement = () => {
  const dispatch = useDispatch();

  const clientViewMode = useSelector(
    (state: RootState) => state.pageViewState.manageClients
  );

  const [value] = useState(0); // Current tab index

  const [, setActiveCount] = useState(0); // Count of active clients
  const [, setInActiveCount] = useState(0); // Count of inactive clients
  const [currentPageGrid, setCurrentPageGrid] = useState<number>(1);
  const [currentPageCard, setCurrentPageCard] = useState<number>(1);
  const [itemsPerPageGrid, setItemsPerPageGrid] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [itemsPerPageCard, setItemsPerPageCard] = useState<number>(12);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tabLabel] = useState(ClientManagementTabStatus.all);
  const [rows, setRows] = useState<ClientData[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [buttonValue, setButtonValue] = useState<string | null>(
    resolveViewButton(clientViewMode)
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);
  const [isFetchClientsInitial, setIsFetchClientsInitial] = useState(true);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const skipFetchOnViewModeAutoSelectRef = useRef(false);
  const hasAutoSelectedViewModeRef = useRef(false);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) +
      INVOICE_REPORT_GRID_ERROR_COMPONENT
  );
  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const {showToast} = useToast();

  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });

  useEffect(() => {
    dispatch(resetClientList());
    dispatch(setSelectedClientId(clientId));
  }, []);

  const buildClientsQueryString = (pageNumber: number, pageSize: number) =>
    `?status=${tabLabel}&name=${searchTerm}&isAdminOnlyView=true&isUserViewOnlyScreen=false&SortColumn=${sortModel[0]?.field ?? ''}&SortOrder=${sortModel[0]?.sort ?? 'asc'}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      setIsFailedToLoadContent(false);
      const isListView = buttonValue === 'list-view';
      const paramString = buildClientsQueryString(
        isListView ? currentPageGrid : currentPageCard,
        isListView ? itemsPerPageGrid : itemsPerPageCard
      );
      const result = await getClientsList(paramString);
      dispatch(setClientListData(result.data.data));
      const clientsList = result.data.data.map(
        (client: ClientData, index: number) => ({
          ...client,
          id: index,
        })
      );
      setRows(clientsList);

      const totalClients = result.data.meta.pagination.totalItems;
      if (
        isFetchClientsInitial &&
        totalClients > TABLE_PER_PAGE_DEFAULT_50_ROW
      ) {
        setItemsPerPageGrid(TABLE_PER_PAGE_DEFAULT_NO_ROW);
        setIsFetchClientsInitial(false);
      }
      setTotalRowCount(totalClients);
      if (!clientViewMode && !hasAutoSelectedViewModeRef.current) {
        hasAutoSelectedViewModeRef.current = true;
        const preferredView =
          result.data.meta.pagination.totalItems > MIN_RECORDS_FOR_CLIENT_GRID
            ? 'list-view'
            : 'grid-view';
        if (preferredView !== buttonValue) {
          skipFetchOnViewModeAutoSelectRef.current = true;
        }
        setViewMode(preferredView);
      }

      if (!isListView) {
        setTimeout(() => {
          scrollToGridView('#client-data-container');
        }, 300);
      }
    } catch (e: any) {
      if (failedToLoadStatusCodes.includes(e.status)) {
        setIsFailedToLoadContent(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };
  useEffect(() => {
    if (skipFetchOnViewModeAutoSelectRef.current) {
      skipFetchOnViewModeAutoSelectRef.current = false;
      return;
    }
    if (tabLabel && buttonValue) {
      fetchClients();
    }
  }, [
    buttonValue,
    value,
    itemsPerPageGrid,
    itemsPerPageCard,
    currentPageCard,
    currentPageGrid,
    searchTerm,
    sortModel,
  ]);
  const setViewMode = (viewMode: string) => {
    setButtonValue(viewMode);
    dispatch(
      setManageClientsPageState(viewMode == 'list-view' ? 'table' : 'tile')
    );
  };

  const activeClientCount = useMemo(
    () =>
      rows?.filter((data: {status: string}) => data.status === 'Active').length,
    [rows]
  );
  const inActiveClientCount = useMemo(
    () =>
      rows?.filter((data: {status: string}) => data.status === 'Inactive')
        .length,
    [rows]
  );
  const clientManagementColumns = useMemo(
    () => [
      ...getClientManagementColumns(),
      {
        field: 'arrow',
        headerName: '',
        minWidth: 48,
        flex: 48 / MANAGE_CLIENTS_TABLE_WIDTH,
        sortable: false,
        headerClassName: 'no-separator',
        isBorderNotRequired: true,
        renderCell: (params: any) => {
          if (!params.row?.hasProjectSettingEnabled) {
            return null;
          }
          return (
            <div className="cell-button">
              <AurButton
                type="text"
                size="Medium"
                ariaLabel={t('clientHomePage.openClientDetails')}
                startIcon={<AurIcon icon={IconNames.ChevronRight} />}
                onClick={() => handleRowClick(params)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRowClick(params);
                  }
                }}
              />
            </div>
          );
        },
      },
    ],
    []
  );

  // Effect hook to update data and counts when relevant state changes
  useEffect(() => {
    setActiveCount(activeClientCount);
    setInActiveCount(inActiveClientCount);
  }, [value, rows]);

  // Handler for toggle button changes
  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    selectedButton: string | null
  ) => {
    if (selectedButton === null || selectedButton === buttonValue) {
      return;
    }

    if (selectedButton === 'grid-view') {
      setCurrentPageCard(1);
    } else {
      setCurrentPageGrid(1);
    }

    setViewMode(selectedButton);
  };

  // Handler for search results and search status changes
  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    setCurrentPageGrid(1);
    setCurrentPageCard(1);
  };

  const navigateToClientDetails = (clientData: ClientData) => {
    if (!clientData?.hasProjectSettingEnabled) {
      showToast(
        `${t('clientDetails.toast.projectSettingsPermission')} ${clientData?.name}`,
        'warning',
        undefined,
        'center'
      );
      return;
    }
    dispatch(setSelectedClientId(clientData.clientId));
    navigate('/clients/details');
  };

  // Handler for row click
  const handleRowClick = (params: GridRowParams<ClientData>) => {
    navigateToClientDetails(params.row);
  };

  // Handler for card click
  const handleClientCardClick = (clientData: ClientCardProps) => {
    navigateToClientDetails(clientData);
  };

  const renderDataTable = () => {
    if (buttonValue === 'grid-view') {
      if (isLoading)
        return (
          <Box className="blank-area-loader">
            <AurLoadingIndicator show={true} />
          </Box>
        );

      if (rows?.length === 0) {
        return <AurNoResults />;
      }

      return (
        <AurCardGrid<ClientCardProps>
          rows={rows}
          pageSizeOptions={[12, 24, 36]}
          entityType={'clients'}
          CardComponent={AurClientCard}
          onClickHandler={handleClientCardClick}
          totalRowCount={totalRowCount}
          currentPage={currentPageCard}
          setCurrentPage={setCurrentPageCard}
          itemsPerPage={itemsPerPageCard}
          setItemsPerPage={setItemsPerPageCard}
        />
      );
    }
    return (
      <div className="table-grid-space">
        <AurDataGrid
          rows={rows}
          columns={clientManagementColumns}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
          visibleToolTip={true}
          checkBoxVisible={false}
          entityType={t('common.clients')}
          className="clientDataGrid"
          currentPage={currentPageGrid}
          setCurrentPage={setCurrentPageGrid}
          itemsPerPage={itemsPerPageGrid}
          setItemsPerPage={setItemsPerPageGrid}
          handleRowClick={handleRowClick}
          pointer={true}
          totalRowCount={totalRowCount}
          sortModel={sortModel}
          setSortModel={onSetSortModel}
          onRefresh={() => fetchClients()}
          containerHeight={size}
          noRowsOverlay={
            isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
          }
          enableRowClickOnEnterPress
          defaultHeaderCellTabIndex={0}
        />
      </div>
    );
  };

  return (
    <div className="client-homepage-main-div">
      <div className="client-homepage-header">
        <Typography
          className="typo-text headline1_size manage-client-style"
          tabIndex={0}
          component="h1"
        >
          {t('clientHomePage.manageClients')} {/* Title */}
        </Typography>
      </div>
      <Box className="client-homepage-content">
        <div className="search-bar-main-div">
          <AurSearchBox
            onSearch={handleSearchChange}
            minCharForSearch={MIN_SEARCH_CHAR}
          />

          {/* Search box component */}
          <ToggleButtonGroup
            className="toggle-group"
            value={buttonValue}
            exclusive
            onChange={handleToggleChange}
          >
            {/* Toggle buttons for view options */}
            <ToggleButton
              value="grid-view"
              aria-label="Card view"
              role="button"
              className="left-toggle"
            >
              <AurIcon icon={IconNames.GridView} />
            </ToggleButton>
            <ToggleButton
              value="list-view"
              aria-label="List view"
              role="button"
              className="right-toggle"
            >
              <AurIcon icon={IconNames.ListView} />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <Box
          className="client-homepage-gridview"
          sx={{borderColor: 'divider', paddingTop: '22px'}}
        ></Box>
        <Box className="client-homepage-gridview" id="client-data-container">
          {/* DataGrid for displaying client data */}
          {renderDataTable()}
        </Box>
      </Box>
      {buttonValue !== 'grid-view' && (
        <AurLoadingIndicator overlay isOpen={isLoading} />
      )}
    </div>
  );
};

export default ClientManagement;
